import { JSX, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { TextField, Grid, Button, Typography, Box, MenuItem, Select } from '@mui/material';

import BackspaceIcon from '@mui/icons-material/Backspace';

import Flag from 'react-country-icons';

import { openSnackbar } from "../store/slices/SnackbarSlice";
import { setPhoneNumber } from '../store/slices/WhatsAppNotificationSlice';

import { ClearFormatPhoneNumber, FormatPhoneNumber } from '../helpers/FormatPhoneNumber';

import { ValidarTelefonoCelularWhatsApp } from '../connections/comun/ServicioConnection';

interface CountryOption {
    code:   string;
    label:  string;
    phone:  string;
    flag:   JSX.Element;
}

type KeyValue = number | 'del';

const countries: CountryOption[] = [
    {
        code: "MX",
        label: "México",
        phone: "+52",
        flag: <Flag size={22} country='MX' />,
    },
    {
        code: "US",
        label: "Estados Unidos",
        phone: "+1",
        flag: <Flag size={22} country='US' />,
    },
];

const keyStyle = {
    height: 90,
    width: 90,
    borderRadius: '50%',
    fontSize: 26,
    fontWeight: 600,
    backgroundColor: '#f5f5f5',
    color: '#212121',
    boxShadow: '0px 5px 5px #bdbdbd',
    '&:hover': {
        backgroundColor: '#eeeeee',
    },
    '&:active': {
        boxShadow: '0 3px 0 #9e9e9e',
        transform: 'translateY(3px)',
    },
};

interface Props {
    onBack:             () => void;
    onActivity:         () => void;
    onSignIn:           () => void;
}

export const Teclado = ( { onBack, onActivity, onSignIn }: Props ) => {

    const dispatch = useDispatch();

    const audioRef = useRef<HTMLAudioElement>(new Audio( process.env.PUBLIC_URL + "/assets/click.wav" ));

    const [loading, setLoading] = useState( false );

    const [telefonoCelular, setTelefonoCelular] = useState( '' );
    const [codigoPais, setCodigoPais] = useState( 'MX' );
    
    const sound = () => {

        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.play();

    }

    const handlePress = ( key: KeyValue ): void => {

        sound();

        if ( key === 'del' ) {
            setTelefonoCelular( (prev) => prev.slice(0, -1) );

        } 
        else if ( telefonoCelular.length < 10 ) {

            if ( telefonoCelular.length === 0 && key === 0 ) return;

            setTelefonoCelular( (prev) => prev + key.toString() );
        }

    };
    
    const handleBack = () => {
        
        sound(); 
        onBack(); 
        dispatch( setPhoneNumber({ phoneNumber: '' }) );

    }

    const handleValidarTelefonoCelular = async () => {

        sound();

        const country = countries.find( (c) => c.code === codigoPais );

        if( !country ){

            dispatch( openSnackbar({ message: 'Seleccione la lada del país de origen', variant: 'warning' })); 
            return;
        }

        setLoading( true );

        const phoneNumber = `${ country.phone }${ ClearFormatPhoneNumber( telefonoCelular ) }`;

        await ValidarTelefonoCelularWhatsApp({ telefonoCelular: phoneNumber }).then( resp => {

            setTimeout(() => {
                
                const { success, message } = resp;
    
                if( success ){
    
                    onSignIn();
                    setLoading( false );

                    dispatch( setPhoneNumber({ phoneNumber: phoneNumber }) );

                }
                else {
                    
                    dispatch( openSnackbar({ message: message, variant: 'error' })); 
                    setLoading( false );

                }

            }, 700);

        });

    }

    return (  
        <Box display='flex' flexDirection='column' gap={2} mt={2}>

            <Grid container spacing={3} mb={2}>

                <Grid size={{ md: 3, xs: 12 }}>
                    
                    <Select
                        sx={{ py: 1 }}
                        fullWidth
                        value={ codigoPais }
                        onChange={ (e) => {
                            const selected = countries.find( (c) => c.code === e.target.value );
                            if ( selected ){ setCodigoPais(selected.code); }

                            onActivity();
                            sound();
                        }}
                        renderValue={ ( value ) => {

                            const country = countries.find( (c) => c.code === value );

                            if( !country){ return <Box> </Box>; }

                            return <Box display="flex" alignItems="center" gap={1}>

                                { country.flag }

                                <Typography variant="body1">
                                    { country.phone }
                                </Typography>

                            </Box>;
                        }}                            
                    >
                        {
                            countries
                            .map( (option) => (
                                <MenuItem key={ option.code} value={ option.code }>

                                    <Box display="flex" alignItems="center" gap={1} my={1}>

                                        { option.flag }

                                        <Typography variant="body2">
                                            { option.label } ({ option.phone })
                                        </Typography>

                                    </Box>

                                </MenuItem>
                            ))
                        }
                    </Select>

                </Grid>
            
                <Grid size={{ md: 9, xs: 12 }}>

                    <TextField
                        fullWidth
                        disabled
                        placeholder="(000) 000-0000"                                                        
                        value={ FormatPhoneNumber( telefonoCelular ) }
                        slotProps={{
                            input: {
                                sx: {
                                    textAlign: "center",
                                    fontSize: 16,
                                    letterSpacing: 6,
                                    borderRadius: 1,
                                    py: 1,
                                },
                            }
                        }}          
                    />  

                </Grid>

            </Grid>            

            <Grid container spacing={3} justifyContent='center'>

                {
                    [1, 2, 3, 4, 5, 6, 7, 8, 9].map(( num ) => (                        
                        <Grid size={4} key={ num } display='flex' justifyContent='center'>
                            <Button
                                sx={ keyStyle }
                                onClick={ () => { handlePress( num ); onActivity(); }}
                            >
                                { num }
                            </Button>
                            
                        </Grid>
                    ))
                }

                <Grid size={4} />

                <Grid size={4} display='flex' justifyContent='center'>
                    <Button sx={ keyStyle } onClick={ () => { handlePress( 0 ); onActivity(); }}>
                        0
                    </Button>
                </Grid>

                <Grid size={4} display='flex' justifyContent='center'>
                    <Button sx={ keyStyle } onClick={ () => { handlePress( 'del' ); onActivity(); }}>
                        <BackspaceIcon fontSize={'large'} />
                    </Button>
                </Grid>

            </Grid>

            <Button
                fullWidth
                variant='contained'
                color='primary'
                sx={{
                    mt: 3,
                    py: 2,
                    fontSize: 20,
                    borderRadius: 2,
                }}
                loading={ loading }
                disabled={ ClearFormatPhoneNumber( telefonoCelular ).length !== 10 }
                onClick={ handleValidarTelefonoCelular }
            >
                Ingresar
            </Button>

            <Button
                fullWidth
                color='primary'
                sx={{
                    py: 2,
                    fontSize: 20,
                    borderRadius: 2,
                }}
                onClick={ handleBack }
            >
                Regresar
            </Button>                  

        </Box>
    );
}
